import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import _ from "lodash";
import {
  getAsset, // Original name used
  allocateAsset, // Original name used
} from "../../actions/asset-management.action";
import { getUserList as getUserListAction } from "../../actions/user-management.action";
import { getProjectList as getProjectListAction } from "../../actions/project-management.action";
import "./scss/asset-management.scss";
import { ASSET_MANAGEMENT } from "../../constants/route-paths";
import DatePickerInput from "../../components/inputs/date-picker";
import Button from 'react-bootstrap/Button';
import SelectInput from "../../components/inputs/select";
import TextAreaInput from "../../components/inputs/textarea-input";
import { useNavigate, useParams } from "react-router-dom";

const allocationType = [
  {
    key: "User",
    value: "USER",
  },
  {
    key: "Project",
    value: "PROJECT",
  },
];

const AllocateAsset = ({ writePermission, ...props }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const params = useParams();
  const assetIdFromParams = params.assetId;

  // Selectors for Redux state
  const user = useSelector(state => state.user.userData);

  const initialFields = useMemo(() => ({
    type: {
      name: "type",
      label: "Allocate To:",
      type: "select",
      touched: false,
      error: false,
      valid: true,
      value: "USER",
      items: allocationType,
      required: true,
    },
    typeId: {
      name: "typeId",
      label: "Id:",
      type: "select",
      touched: false,
      error: false,
      valid: true,
      value: "",
      items: [],
      required: true,
    },
    allocationDate: {
      name: "allocationDate",
      label: "Allocation Date:",
      type: "datepicker",
      touched: false,
      error: false,
      valid: true,
      value: new Date(),
    },
    returnDate: {
      name: "returnDate",
      label: "Return Date:",
      type: "datepicker",
      touched: false,
      error: false,
      valid: true,
      value: "",
      minDate: new Date(),
    },
    description: {
      name: "description",
      label: "Description:",
      type: "textarea",
      touched: false,
      error: false,
      valid: true,
      value: "",
    },
  }), []);

  const [fields, setFields] = useState(initialFields);
  const [assetData, setAssetData] = useState({});
  const [typeName, setTypeName] = useState("");

  // Helper function to fetch and format User list with ERROR HANDLING
  const getUserList = useCallback(async () => {
    try {
      const users = await dispatch(getUserListAction());
      let assetOptions = users.map((user) => ({
        key:
          (user.firstName && user.firstName !== null ? user.firstName : "") +
          " " +
          (user.lastName && user.lastName !== null ? user.lastName : ""),
        value: user.id,
      }));

      assetOptions = _.orderBy(
        assetOptions,
        [({ key }) => key.toLowerCase()],
        ["asc"]
      );
      return assetOptions;
    } catch (error) {
      console.error("Error fetching user list:", error);
      // Return empty array on failure
      return [];
    }
  }, [dispatch]);

  // Helper function to fetch and format Project list with ERROR HANDLING
  const getProjectList = useCallback(async () => {
    try {
      const projects = await dispatch(getProjectListAction());
      let assetOptions = projects
        .filter((asset) => asset.type === "PROJECT")
        .map((project) => ({
          key: project.name,
          value: project.id,
        }));

      assetOptions = _.orderBy(
        assetOptions,
        [({ key }) => key.toLowerCase()],
        ["asc"]
      );
      return assetOptions;
    } catch (error) {
      console.error("Error fetching project list:", error);
      // Return empty array on failure
      return [];
    }
  }, [dispatch]);

  // Load initial data on mount with ERROR HANDLING
  useEffect(() => {
    const loadInitialData = async () => {
      if (assetIdFromParams) {
        try {
          // Fetch asset data and initial user list
          const asset = await dispatch(getAsset(assetIdFromParams));
          const userOptions = await getUserList();

          setAssetData(asset);

          // Update fields with initial user options
          setFields(prevFields => ({
            ...prevFields,
            typeId: { ...prevFields.typeId, items: userOptions },
          }));
        } catch (error) {
          console.error("Failed to load initial asset data:", error);
          // Handle display error to user here
        }
      }
    };
    loadInitialData();
  }, [assetIdFromParams, dispatch, getAsset, getUserList]);


  // Input change handler
  const inputChangeHandler = useCallback(async (e, fieldName = "") => {
    setFields(prevFields => {
      const newFields = { ...prevFields };

      if (fieldName === "returnDate" || fieldName === "allocationDate") {
        if (fieldName === "allocationDate") {
          newFields["returnDate"].value = null;
          newFields["returnDate"].minDate = new Date(e);
        }
        newFields[fieldName].value = e;
        newFields[fieldName].touched = true;
      } else if (fieldName === "type") {
        const newValue = e.target.value;
        newFields[fieldName].value = newValue;
        newFields[fieldName].touched = true;

        // Reset typeId value
        newFields["typeId"].value = "";
        newFields["typeId"].items = [];

        // Load items dynamically based on the new type
        // Note: getUserList/getProjectList already contain internal try...catch
        const loadItems = async () => {
          let items = [];
          if (newValue === "USER") {
            items = await getUserList();
          } else if (newValue === "PROJECT") {
            items = await getProjectList();
          }

          // Update state with new items after async operation completes
          setFields(currentFields => ({
            ...currentFields,
            typeId: { ...currentFields.typeId, items: items }
          }));
        };

        loadItems();

      } else if (fieldName === "typeId") {
        newFields[fieldName].value = e.target.value;
        newFields[fieldName].touched = true;

        // Find the key for typeName and update its state
        const selectedItem = newFields[fieldName].items.find(item => item.value === e.target.value);
        if (selectedItem) {
          setTypeName(selectedItem.key);
        }
      } else if (_.isObject(e) && _.isObject(e.target)) {
        newFields[e.target.name].value = e.target.value;
        newFields[e.target.name].touched = true;
      }

      return newFields;
    });
  }, [getUserList, getProjectList]);

  // Validation handler
  const handleValidation = useCallback(({ value, required }) => {
    if (required && (_.isEmpty(value) && value !== 0)) {
      return { error: "This Field is Required", valid: false };
    }
    return { error: false, valid: true };
  }, []);

  // Form submission handler with ERROR HANDLING
  const submitForm = async () => {
    let isFormValid = true;
    let newFields = { ...fields };

    // Validate all fields
    for (let key in newFields) {
      const { error, valid } = handleValidation(newFields[key]);
      newFields[key].touched = true;
      newFields[key].valid = valid;
      newFields[key].error = error;
      isFormValid = isFormValid && valid;
    }

    if (!isFormValid) {
      setFields(newFields); // Trigger re-render to show errors
      return;
    }

    const data = {
      type: newFields.type.value,
      typeId: newFields.typeId.value,
      allocationDate: newFields.allocationDate.value,
      returnDate: newFields.returnDate.value,
      description: newFields.description.value,
      assetId: assetIdFromParams,
      typeName: typeName,
      allocatedBy: user.id,
    };

    // Add try...catch for the allocation action
    try {
      await dispatch(allocateAsset(data));
      handleClose();
    } catch (error) {
      console.error("Failed to allocate asset:", error);
      // IMPORTANT: Display a notification to the user here that allocation failed.
    }
  };

  // Close handler (navigation)
  const handleClose = useCallback(() => {
    navigate(`/${ASSET_MANAGEMENT}`);
  }, [navigate]);

  return (
    <div className="purchase-order-block">
      {/* --- Heading --- */}
      <div className="row">
        <div className="w-auto">
          <h2 className="content-heading text-uppercase">Allocate Assets</h2>
        </div>
      </div>

      {/* --- Asset Info and Form Fields --- */}
      <div className="row">
        <div className="col-lg-6 mt-3">
          <div className="view-block">
            <label>Asset Name</label>
            <p>{assetData.name}</p>
          </div>
        </div>
        <div className="col-lg-6 mt-3">
          <div className="view-block">
            <label>Asset Id</label>
            <p>{assetData.assetNo}</p>
          </div>
        </div>

        <div className="col-lg-6 mt-3">
          <SelectInput
            {...fields.type}
            onChange={(e) => inputChangeHandler(e, fields.type.name)}
          />
        </div>
        <div className="col-lg-6 mt-3">
          <SelectInput
            {...fields.typeId}
            onChange={(e) => inputChangeHandler(e, fields.typeId.name)}
          />
        </div>
        <div className="col-lg-6 mt-3">
          <DatePickerInput
            {...fields.allocationDate}
            onChange={(e) => inputChangeHandler(e, fields.allocationDate.name)}
          />
        </div>
        <div className="col-lg-6 mt-3">
          <DatePickerInput
            {...fields.returnDate}
            onChange={(e) => inputChangeHandler(e, fields.returnDate.name)}
          />
        </div>
        <div className="col-12 mt-3">
          <TextAreaInput
            {...fields.description}
            label={'Description'}
            onChange={(e) => inputChangeHandler(e, fields.description.name)}
          />
        </div>
      </div>

      {/* --- Buttons --- */}
      <div className="col-12 d-flex justify-content-end gap-3 mt-3">
        {writePermission && (
          <Button onClick={submitForm} className="secondarybtn">Allocate</Button>
        )}
        <Button onClick={handleClose} className="secondarybtn cancelbtn">CANCEL</Button>
      </div>
    </div>
  );
};

export default AllocateAsset;